import * as React from 'react';

import axios from 'axios';

import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import { CustomTextLink } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/CustomTextLink';
import {
  NuorderOrderLineItemRequest,
  NuorderProductOrderable,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';

/**
 * Splits line items based on product availability across different warehouses.
 * if the product cannot be fulfilled by a single warehouse, it will be split into multiple line items.
 * If the product is not available in any warehouse, it will be added as a single line item with the full quantity.
 */
export const splitLineItemsPerAvailability = (
  lineItems: NuorderOrderLineItemRequest[],
  nuorderProductAvailability: Record<NuorderProductOrderable['id'], NuorderProductOrderable>
): NuorderOrderLineItemRequest[] => {
  return lineItems.reduce<NuorderOrderLineItemRequest[]>((acc, lineItem) => {
    let quantityToFulfill = lineItem.quantity;
    const product = nuorderProductAvailability[lineItem.product_id];
    if (product && product.inventory && product.inventory.length > 0) {
      // sort warehouses by highest quantity first
      const inventory = [...product.inventory]
        .filter((warehouse) => warehouse.quantity && warehouse.quantity > 0)
        .sort((a, b) => (a.quantity! < b.quantity! ? 1 : -1));
      // split the purchase line until the quantity is fulfilled or no more inventory is available
      acc.push(
        ...inventory.reduce<NuorderOrderLineItemRequest[]>((acc, warehouse) => {
          if (quantityToFulfill <= 0) return acc;
          const quantity = Math.min(quantityToFulfill, warehouse.quantity!);
          quantityToFulfill -= quantity;
          acc.push({
            product_id: lineItem.product_id,
            quantity,
            warehouse_id: warehouse.warehouse_id,
          });
          return acc;
        }, [])
      );
      if (quantityToFulfill > 0) {
        // If there is still quantity to fulfill, add it as a separate line item
        acc.push({
          product_id: lineItem.product_id,
          quantity: quantityToFulfill,
        });
      }
      return acc;
    }
    // If no inventory is available, just add the line item with the full quantity
    acc.push({
      product_id: lineItem.product_id,
      quantity: lineItem.quantity,
    });
    return acc;
  }, []);
};

export const getErrorMessageFromNuorderError = (error: unknown): string => {
  if (error && axios.isAxiosError(error)) {
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.data) return JSON.stringify(error.response.data);
  }
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) return JSON.stringify(error);
  return 'An unknown error occurred';
};

export const getErrorStatusCodeFromNuorderError = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
};

interface ComputeAlertMessageFromNuorderErrorMessageParams {
  errorMessage: string;
  errorStatusCode?: number;
  vendorLink?: string;
  vendorLinkingLink?: string;
  viewOnNuorderLink?: string;
  isCompletionError?: boolean;
}

export const computeAlertMessageFromNuorderErrorMessage = ({
  errorMessage,
  errorStatusCode,
  vendorLink,
  vendorLinkingLink,
  viewOnNuorderLink,
  isCompletionError = false,
}: ComputeAlertMessageFromNuorderErrorMessageParams): AlertMessageDTO => {
  if (/currency[\s\S]*code/i.test(errorMessage)) {
    /**
     * This pattern extracts a value (like a currency code) that is enclosed in quotes (single, double, or backtick)
     * after the phrase "available currency code(s)". The value is captured in the first group.
     */
    const currencies = errorMessage
      .match(/available currency codes?[^'"`]*['"`]([^'"`]+)['"`]/i)?.[1]
      ?.split(/,\s*/);
    return getAlertMessageByType(SendToNuorderAlertMessageTypes.INVALID_CURRENCY_CODE, {
      href: vendorLink,
      vars: currencies?.length ? currencies : undefined,
    });
  }
  if (/shipping[\s\S]*address/i.test(errorMessage)) {
    return getAlertMessageByType(SendToNuorderAlertMessageTypes.INVALID_SHIPPING_ADDRESS, {
      href: vendorLinkingLink,
    });
  }
  if (/forbidden[\s\S]*inventory/i.test(errorMessage)) {
    return getAlertMessageByType(SendToNuorderAlertMessageTypes.INVALID_INVENTORY, {
      href: viewOnNuorderLink,
    });
  }
  if (/invalid order[\s\S]*must[\s\S]*uuid/i.test(errorMessage)) {
    return getAlertMessageByType(SendToNuorderAlertMessageTypes.INVALID_PRODUCT_UUID);
  }
  if (isCompletionError) {
    return getAlertMessageByType(SendToNuorderAlertMessageTypes.GENERIC_COMPLETION_ERROR, {
      vars:
        errorStatusCode === 403 && errorMessage && errorMessage[0] !== '{'
          ? [errorMessage]
          : undefined,
      href: viewOnNuorderLink,
    });
  }
  return getAlertMessageByType(SendToNuorderAlertMessageTypes.GENERIC_ERROR);
};

export const SendToNuorderAlertMessageTypes = {
  NOT_READY: 'not_ready',
  MISSING_CURRENCY_CODE: 'missing_currency_code',
  MISSING_SHIPPING_ADDRESS: 'missing_shipping_address',
  SUCCESS_COMPLETE_ORDER: 'success_complete_order',
  INVALID_CURRENCY_CODE: 'invalid_currency_code',
  INVALID_SHIPPING_ADDRESS: 'invalid_shipping_address',
  INVALID_INVENTORY: 'invalid_inventory',
  INVALID_PRODUCT_UUID: 'invalid_product_uuid',
  GENERIC_ERROR: 'generic_error',
  GENERIC_COMPLETION_ERROR: 'generic_completion_error',
} as const;

export type SendToNuorderAlertMessageType =
  (typeof SendToNuorderAlertMessageTypes)[keyof typeof SendToNuorderAlertMessageTypes];

export interface GetAlertMessageByTypeOptions {
  href?: string;
  vars?: string[];
}

export const getAlertMessageByType = (
  type: SendToNuorderAlertMessageType,
  { href, vars }: GetAlertMessageByTypeOptions = {}
): AlertMessageDTO => {
  switch (type) {
    case SendToNuorderAlertMessageTypes.NOT_READY:
      return {
        type: 'danger',
        title: 'Cannot send to NuORDER',
        message: `Some items contain errors that prevent sending to NuORDER.
Update the item details or remove item from your order to resolve.`,
      };
    case SendToNuorderAlertMessageTypes.MISSING_CURRENCY_CODE:
      return {
        type: 'danger',
        title: 'Cannot send to NuORDER',
        message: (
          <>
            Vendor does not have a purchasing currency set.
            {href && (
              <>
                <br />
                <CustomTextLink href={href} text="Set vendor currency" />
              </>
            )}
          </>
        ),
      };
    case SendToNuorderAlertMessageTypes.MISSING_SHIPPING_ADDRESS:
      return {
        type: 'danger',
        title: 'Cannot send to NuORDER',
        message: (
          <>
            Shop does not have a NuORDER shipping address set. Please ensure that a NuORDER shipping
            address has been mapped to the Shop location.
            {href && (
              <>
                <br />
                <CustomTextLink href={href} text="Set vendor shipping address" />
              </>
            )}
          </>
        ),
      };
    case SendToNuorderAlertMessageTypes.SUCCESS_COMPLETE_ORDER:
      return {
        type: 'success',
        title: 'Order placed on NuORDER',
        message: (
          <>
            Order was successfully placed on NuORDER. Any future changes to this purchase order must
            be made through NuORDER and will not be reflected on this page.
            {href && (
              <>
                <br />
                <CustomTextLink href={href} text="View on NuORDER" blank={true} />
              </>
            )}
          </>
        ),
      };
    case SendToNuorderAlertMessageTypes.INVALID_CURRENCY_CODE:
      return {
        type: 'danger',
        title: 'Error sending order to NuORDER',
        message: (
          <>
            Order creation failed due to purchasing currency mismatch between local vendor and
            NuORDER vendor.
            {vars && (
              <>
                <br />
                NuORDER vendor accepts orders in the following currencies:{' '}
                <strong>
                  <var>{vars?.join(', ')}.</var>
                </strong>
                <br />
                Please set your vendor purchasing currency accordingly.
              </>
            )}
            {href && (
              <>
                <br />
                <CustomTextLink href={href} text="Change Vendor currency" />
              </>
            )}
          </>
        ),
      };
    case SendToNuorderAlertMessageTypes.INVALID_SHIPPING_ADDRESS:
      return {
        type: 'danger',
        title: 'Error sending order to NuORDER',
        message: (
          <>
            Order creation failed due to missing or invalid shipping address.
            <br />
            Please ensure that a NuORDER shipping address has been mapped to the Shop location.
            {href && (
              <>
                <br />
                <CustomTextLink href={href} text="Set vendor shipping address" />
              </>
            )}
          </>
        ),
      };
    case SendToNuorderAlertMessageTypes.INVALID_INVENTORY:
      return {
        type: 'warning',
        title: 'Could not finalize order',
        message: (
          <>
            Order finalization failed due to unavailable inventory information for one or more
            items.
            <br />
            Please ensure all items have a valid UPC that returns NuORDER availability information.
            {href && (
              <p>
                You can also try finalizing the draft order on NuORDER directly.
                <br />
                <CustomTextLink href={href} text="View on NuORDER" blank={true} />
              </p>
            )}
          </>
        ),
      };
    case SendToNuorderAlertMessageTypes.INVALID_PRODUCT_UUID:
      return {
        type: 'danger',
        title: 'Error sending order to NuORDER',
        message: `Order creation failed due to an item not found on NuORDER.
Update the item details or remove item from your order to resolve.`,
      };
    case SendToNuorderAlertMessageTypes.GENERIC_ERROR:
      return {
        type: 'danger',
        title: 'Error sending order to NuORDER',
        message:
          'An unknown error occurred while sending the order to NuORDER. Please try again later.',
      };
    case SendToNuorderAlertMessageTypes.GENERIC_COMPLETION_ERROR:
      return {
        type: 'warning',
        title: 'Could not finalize order',
        message: (
          <>
            {vars?.[0] ?? 'Order finalization failed.'}
            {href && (
              <p>
                You can also try finalizing the draft order on NuORDER directly.
                <br />
                <CustomTextLink href={href} text="View on NuORDER" blank={true} />
              </p>
            )}
          </>
        ),
      };
    default:
      throw new Error(`Unknown alert message type: ${type}`);
  }
};
